import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { authTokenManager as defaultTokenManager } from "./authTokenManager";
import { decodeL500Token } from "./decodeL500Token";
function msUntilRefresh(l500Token, refreshLeadTimeMs, minRefreshDelayMs) {
    const decoded = decodeL500Token(l500Token);
    if (!decoded)
        return null;
    const expMs = decoded.exp * 1000;
    const lead = expMs - Date.now() - refreshLeadTimeMs;
    return Math.max(lead, minRefreshDelayMs);
}
function clearRefreshTimer(timerRef) {
    if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
    }
}
export function DevAuthWrapper({ tokenExchangeUrl, buildExchangeBody, email, children, tokenManager = defaultTokenManager, refreshLeadTimeMs = 60_000, minRefreshDelayMs = 1_000, renderLoading, renderError, onStateChange, }) {
    const [tokenState, setTokenState] = useState("idle");
    const [tokenError, setTokenError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const refreshTimerRef = useRef(null);
    function transitionTo(nextState) {
        setTokenState(nextState);
        onStateChange?.(nextState);
    }
    useEffect(() => {
        let cancelled = false;
        transitionTo("exchanging");
        setTokenError(null);
        async function attemptExchange() {
            try {
                const response = await fetch(tokenExchangeUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(buildExchangeBody(email)),
                });
                if (!response.ok) {
                    const body = await response.text();
                    return {
                        ok: false,
                        error: new Error(`Dev token request failed: ${response.status} ${body}`),
                        // Retry transient server errors; fail fast on 4xx (genuine auth failure).
                        retryable: response.status >= 500,
                    };
                }
                const data = (await response.json());
                return { ok: true, token: data.token };
            }
            catch (err) {
                // Network/abort errors are transient.
                return {
                    ok: false,
                    error: err instanceof Error ? err : new Error("Dev token request failed"),
                    retryable: true,
                };
            }
        }
        async function performExchange() {
            if (cancelled)
                return;
            const maxAttempts = 4;
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                if (cancelled)
                    return;
                const result = await attemptExchange();
                if (cancelled)
                    return;
                if (result.ok) {
                    tokenManager.setToken(result.token);
                    transitionTo("ready");
                    scheduleRefresh(result.token);
                    return;
                }
                if (result.retryable && attempt < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 300 * attempt));
                    continue;
                }
                tokenManager.clearToken();
                setTokenError(result.error);
                transitionTo("error");
                return;
            }
        }
        function scheduleRefresh(l500Token) {
            clearRefreshTimer(refreshTimerRef);
            const delay = msUntilRefresh(l500Token, refreshLeadTimeMs, minRefreshDelayMs);
            if (delay === null)
                return;
            refreshTimerRef.current = window.setTimeout(() => {
                refreshTimerRef.current = null;
                void performExchange();
            }, delay);
        }
        void performExchange();
        return () => {
            cancelled = true;
            clearRefreshTimer(refreshTimerRef);
        };
    }, [retryCount]);
    if (tokenState === "error") {
        if (renderError && tokenError) {
            return _jsx(_Fragment, { children: renderError(tokenError, () => setRetryCount(c => c + 1)) });
        }
        return null;
    }
    if (tokenState !== "ready") {
        return renderLoading ? _jsx(_Fragment, { children: renderLoading() }) : null;
    }
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=DevAuthWrapper.js.map